import { forwardRef, type ButtonHTMLAttributes, type ElementType, type ReactNode } from "react";
import { Spinner } from "./Spinner";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <Button>. Implementation
// and CSS ported letter-for-letter from the vendor's compiled bundle and
// Button-M3ZWLYUF.css (read from node_modules, not guessed) — same
// disabled/loading/as-polymorphism behavior, same --ds-* CSS references,
// new .rc-button* class names.
//
// `as` polymorphism matters here specifically: when rendered as something
// other than a native <button> (an anchor, a routing Link), `disabled`
// can't use the native attribute, so it becomes aria-disabled + a data
// attribute + a synthetic click-blocker — the vendor does exactly this,
// so this does too, rather than silently dropping disabled behavior for
// every `<Button as="a">`/`<Button as={Link}>` call site.
//
export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "gradient";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  as?: ElementType;
  href?: string;
  target?: string;
  rel?: string;
  children: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading = false, leadingIcon, trailingIcon, className, disabled, children, type = "button", as, ...rest },
  ref
) {
  const isDisabled = disabled || loading;
  const Element = as ?? "button";
  const isNativeButton = Element === "button";

  // Built as one literal and kept out of the spread that carries `ref` —
  // passing a ref-bearing plain object into a generic call (createElement,
  // or even a spread that mixes `ref` into a variable) trips this app's
  // react-hooks/refs lint rule, which can't statically verify `ref` gets
  // React's special handling there. Passing it as a direct JSX attribute on
  // the dynamic `Element` tag (a capitalized variable, so JSX treats its
  // runtime value as the element type — the standard polymorphic-component
  // pattern) is what the rule is actually designed to recognize as safe.
  const variantProps = isNativeButton
    ? { type, disabled: isDisabled }
    : isDisabled
      ? {
          "aria-disabled": true,
          "data-ds-disabled": true,
          tabIndex: -1,
          onClick: (e: React.MouseEvent) => e.preventDefault(),
        }
      : {};

  return (
    <Element
      ref={ref}
      aria-busy={loading || undefined}
      data-ds-loading={loading || undefined}
      className={cn("rc-button", `rc-button--${variant}`, `rc-button--${size}`, className)}
      {...rest}
      {...variantProps}
    >
      <span className="rc-button__inner">
        {loading ? (
          <span className="rc-button__spinner" aria-hidden="true">
            <Spinner />
          </span>
        ) : null}
        <span className="rc-button__content">
          {leadingIcon}
          {children}
          {trailingIcon}
        </span>
      </span>
    </Element>
  );
});
